import {
  Button,
  DetailList,
  formatDate,
  formatDateTime,
  formatPhone,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@majitap/ui/arc/tabs/tabs";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { APPROVAL_STATUS } from "../../labels";
import {
  PARTNER_ROLES,
  type Partner,
  type PartnerMember,
  type PartnerRole,
  partnerActions,
  partnerActionsFor,
  partnerQuery,
} from "../../partners";
import { meQuery } from "../../session";
import { ConfirmDialog } from "../shared/confirm";
import { IntegrationLinks } from "../shared/integration-links";
import { ActivityPanel, WalletPanel } from "../shared/panels";
import { PartnerFleet } from "./fleet";
import { PartnerDialog } from "./list";
import styles from "./partners.module.css";
import { PayoutPanel } from "./payout";

export const PARTNER_TABS = ["overview", "members", "fleet", "payout", "wallet", "activity"] as const;
type PartnerTab = (typeof PARTNER_TABS)[number];

/**
 * One partner (P8-13): onboarding from pending to approved, its people, its fleet, its payout
 * details (a second person approves them), its wallet and history.
 */
export function PartnerPage(): ReactNode {
  const { partnerId } = useParams({ from: "/console/partners/$partnerId" });
  const search = useSearch({ from: "/console/partners/$partnerId" });
  const navigate = useNavigate({ from: "/partners/$partnerId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const partner = useQuery(partnerQuery(partnerId));
  const [dialog, setDialog] = useState<"edit" | "reject" | "suspend" | "restore" | null>(null);
  useRecordCrumb(partner.data?.name);

  if (partner.status === "pending") return <PageLoading label="Loading the partner" />;
  if (partner.status === "error") {
    const error = errorOf(partner.error);
    if (error.code === "PARTNER_NOT_FOUND")
      return <PageEmpty title="No such partner" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => partner.refetch()} />;
  }
  const p = partner.data;
  const tabs: { id: PartnerTab; label: string; shown: boolean }[] = [
    { id: "overview", label: "Overview", shown: true },
    { id: "members", label: "Members", shown: true },
    { id: "fleet", label: "Fleet", shown: true },
    { id: "payout", label: "Payout details", shown: true },
    { id: "wallet", label: "Wallet", shown: permissions.includes("payments.record") },
    { id: "activity", label: "Activity", shown: permissions.includes("staff.manage") },
  ];
  const visible = tabs.filter((t) => t.shown);
  const tab = visible.some((t) => t.id === search.tab) ? (search.tab as PartnerTab) : "overview";
  const [statusLabel, statusTone] = APPROVAL_STATUS[p.approval_status];

  return (
    <>
      <PageHeader
        title={p.name}
        description={[
          statusLabel,
          p.members.length === 1 ? "1 member" : `${p.members.length} members`,
          p.tax_pin ? `Tax PIN ${p.tax_pin}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <div className={styles.actions}>
            <Lifecycle
              partner={p}
              onReject={() => setDialog("reject")}
              onSuspend={() => setDialog("suspend")}
              onRestore={() => setDialog("restore")}
            />
            <Button variant="secondary" onClick={() => setDialog("edit")}>
              Edit
            </Button>
          </div>
        }
      />
      {p.approval_status === "pending" ? (
        <Alert tone="info" title="Waiting for approval">
          Its people can't sign in to the partner portal, and its fleet can't be dispatched, until it's approved.
        </Alert>
      ) : null}
      {p.approval_status === "suspended" ? (
        <Alert tone="warning" title="Suspended">
          Its people and drivers are signed out and can't sign in, and Dispatch doesn't use its fleet. Approve it to
          restore them.
        </Alert>
      ) : null}
      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({ search: { tab: v === "overview" ? undefined : (v as PartnerTab) }, replace: true })
        }
      >
        <TabsList aria-label="About this partner">
          {visible.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
              {t.id === "payout" && p.payout.method && !p.payout.approved ? (
                <Badge tone="warning">To approve</Badge>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <Overview partner={p} statusBadge={<Badge tone={statusTone}>{statusLabel}</Badge>} />
          <IntegrationLinks owner={{ partner_id: p.id }} />
        </TabsContent>
        <TabsContent value="members">
          <Members partner={p} />
        </TabsContent>
        <TabsContent value="fleet">
          {tab === "fleet" ? <PartnerFleet partnerId={p.id} canAttach={permissions.includes("fleet.manage")} /> : null}
        </TabsContent>
        <TabsContent value="payout">
          {tab === "payout" ? (
            <PayoutPanel partner={p} canReadHistory={permissions.includes("staff.manage")} meId={me?.user.id} />
          ) : null}
        </TabsContent>
        <TabsContent value="wallet">
          {tab === "wallet" ? <WalletPanel owner={{ partner_id: p.id }} /> : null}
        </TabsContent>
        <TabsContent value="activity">
          {tab === "activity" ? (
            <ActivityPanel targetType="partner" id={p.id} label="What changed for this partner" />
          ) : null}
        </TabsContent>
      </Tabs>
      {dialog === "edit" ? <PartnerDialog partner={p} onClose={() => setDialog(null)} /> : null}
      {dialog === "reject" || dialog === "suspend" || dialog === "restore" ? (
        <ReasonDialog partner={p} kind={dialog} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

function useRefreshPartner(id: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["partner", id] });
    await queryClient.invalidateQueries({ queryKey: ["partners"] });
  };
}

/**
 * Approving a pending partner confirms in place; reject, suspend and restoring a suspended one ask why.
 * Steps the lifecycle doesn't allow aren't offered (`INVALID_PARTNER_TRANSITION`).
 */
function Lifecycle({
  partner: p,
  onReject,
  onSuspend,
  onRestore,
}: {
  partner: Partner;
  onReject: () => void;
  onSuspend: () => void;
  onRestore: () => void;
}) {
  const refresh = useRefreshPartner(p.id);
  const can = partnerActionsFor(p.approval_status);
  return (
    <>
      {can.reject ? (
        <Button variant="secondary" onClick={onReject}>
          Reject
        </Button>
      ) : null}
      {can.suspend ? (
        <Button variant="secondary" onClick={onSuspend}>
          Suspend
        </Button>
      ) : null}
      {can.approve && p.approval_status === "suspended" ? (
        <Button onClick={onRestore}>Approve</Button>
      ) : can.approve ? (
        <ConfirmMorph
          label="Approve"
          tone="neutral"
          prompt="Approve this partner?"
          confirmLabel="Approve"
          pendingLabel="Approving"
          doneLabel="Approved"
          onConfirm={async () => {
            await partnerActions.approve(p.id);
            await refresh();
          }}
          resultTimeout={1500}
        />
      ) : null}
    </>
  );
}

const REASON_COPY = {
  reject: {
    title: "Reject",
    description: "Its people can't sign in. The reason is recorded.",
    keep: "Keep it pending",
    failed: "Not rejected",
  },
  suspend: {
    title: "Suspend",
    description:
      "Its people are signed out and can't sign in; its drivers' shifts close. Approve it again to undo it. The reason is recorded.",
    keep: "Keep it active",
    failed: "Not suspended",
  },
  restore: {
    title: "Approve",
    description:
      "Its people can sign in to the partner portal again, and its fleet can be dispatched. The reason is recorded.",
    keep: "Keep it suspended",
    failed: "Not approved",
  },
} as const;

/** Reject, suspend, or approve a suspended partner again, with a reason that goes into its history. */
function ReasonDialog({
  partner,
  kind,
  onClose,
}: {
  partner: Partner;
  kind: keyof typeof REASON_COPY;
  onClose: () => void;
}) {
  const refresh = useRefreshPartner(partner.id);
  const [reason, setReason] = useState("");
  const copy = REASON_COPY[kind];
  const mutation = useMutation({
    mutationFn: () => {
      const why = reason.trim() || undefined;
      if (kind === "reject") return partnerActions.reject(partner.id, why);
      if (kind === "suspend") return partnerActions.suspend(partner.id, why);
      return partnerActions.approve(partner.id, why);
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`${copy.title} ${partner.name}?`} description={copy.description}>
        <div className={styles.form}>
          <Textarea label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          {mutation.error ? (
            <Alert tone="danger" title={copy.failed}>
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              {copy.keep}
            </Button>
            <Button
              variant={kind === "restore" ? "primary" : "danger"}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {copy.title}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Overview({ partner: p, statusBadge }: { partner: Partner; statusBadge: ReactNode }) {
  const format = useCompanyFormat();
  const terms = useQuery(paymentTermsQuery);
  return (
    <div className={styles.facts}>
      <DetailList
        items={[
          { label: "Status", value: statusBadge },
          { label: "Approved", value: p.approved_at ? formatDateTime(p.approved_at, format) : null },
          { label: "Phone", value: formatPhone(p.phone) },
          { label: "Email", value: p.email },
          { label: "Tax PIN", value: p.tax_pin },
          {
            label: "Payout terms",
            value: terms.data?.find((t) => t.id === p.payment_term_id)?.name ?? "The company's default",
          },
          { label: "eTIMS invoice", value: p.etims_invoice_required ? "Needed on payout requests" : "Not needed" },
          { label: "Added", value: formatDate(p.created_at, format) },
        ]}
      />
    </div>
  );
}

const ROLE_KEYS = Object.keys(PARTNER_ROLES) as PartnerRole[];

/** The partner's people. They sign in to the partner portal by phone; owners run the account. */
function Members({ partner: p }: { partner: Partner }) {
  const refresh = useRefreshPartner(p.id);
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string>();
  const change = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: { role?: PartnerRole; status?: "active" | "inactive" } }) =>
      partnerActions.updateMember(p.id, userId, body),
    onSuccess: async () => {
      setProblem(undefined);
      await refresh();
    },
    onError: (e) => setProblem(messageFor(errorOf(e))),
  });
  const [removing, setRemoving] = useState<PartnerMember | null>(null);
  const menu = (m: PartnerMember): DropdownItem[] => [
    ...ROLE_KEYS.filter((r) => r !== m.role).map((role) => ({
      label: `Make ${PARTNER_ROLES[role].label.toLowerCase()}`,
      onSelect: () => change.mutate({ userId: m.user_id, body: { role } }),
    })),
    m.status === "active"
      ? {
          label: "Deactivate",
          separatorBefore: true,
          onSelect: () => change.mutate({ userId: m.user_id, body: { status: "inactive" } }),
        }
      : {
          label: "Reactivate",
          separatorBefore: true,
          onSelect: () => change.mutate({ userId: m.user_id, body: { status: "active" } }),
        },
    { label: "Remove", destructive: true, onSelect: () => setRemoving(m) },
  ];
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>
          Owners change the account and its members; managers change the fleet; members only look.
        </span>
        <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
          Add a member
        </Button>
      </div>
      {problem ? (
        <Alert tone="danger" title="That didn't work">
          {problem}
        </Alert>
      ) : null}
      <SortableDataTable
        caption="Members"
        rowKey="user_id"
        rows={p.members}
        itemName={{ one: "member", other: "members" }}
        emptyMessage="No members yet. Add an owner so someone can run the account."
        columns={[
          {
            key: "name",
            label: "Member",
            sortable: false,
            render: (_v, m) => (
              <span className={styles.stack}>
                <span>{m.name}</span>
                {m.email ? <span className={styles.muted}>{m.email}</span> : null}
              </span>
            ),
          },
          {
            key: "phone_number",
            label: "Phone",
            sortable: false,
            render: (_v, m) => formatPhone(m.phone_number) || "—",
          },
          { key: "role", label: "Role", sortable: false, render: (_v, m) => PARTNER_ROLES[m.role].label },
          {
            key: "status",
            label: "Status",
            sortable: false,
            render: (_v, m) => (
              <Badge tone={m.status === "active" ? "success" : "neutral"}>
                {m.status === "active" ? "Active" : m.status === "inactive" ? "Inactive" : "Suspended"}
              </Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            sortable: false,
            render: (_v, m) => <DropdownMenu label="Actions" items={menu(m)} />,
          },
        ]}
      />
      {adding ? <AddMemberDialog partner={p} onClose={() => setAdding(false)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={`Remove ${removing.name}?`}
          description={`They can no longer sign in to the partner portal for ${p.name}. You can add them again later.`}
          action="Remove member"
          keep="Keep"
          danger
          run={() => partnerActions.removeMember(p.id, removing.user_id)}
          onDone={async () => {
            setProblem(undefined);
            await refresh();
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}

function AddMemberDialog({ partner, onClose }: { partner: Partner; onClose: () => void }) {
  const refresh = useRefreshPartner(partner.id);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<PartnerRole>(partner.members.length === 0 ? "owner" : "member");
  const mutation = useMutation({
    mutationFn: () => partnerActions.addMember(partner.id, { phone_number: phone.trim(), name: name.trim(), role }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Add someone to ${partner.name}`}
        description="They get an SMS and sign in to the partner portal with this number."
      >
        <div className={styles.form}>
          <Input
            label="Phone number"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+254712345678"
          />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label="Role"
            value={role}
            onValueChange={(v) => setRole(v as PartnerRole)}
            description={PARTNER_ROLES[role].help}
            options={ROLE_KEYS.map((r) => ({ value: r, label: PARTNER_ROLES[r].label }))}
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not added">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!phone.trim() || !name.trim()}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Add member
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
